import { ErrorState } from '@/components/full-page-state';
import { formatMoney, formatShortDate } from '@/shared/lib/format';
import { Button } from '@/shared/ui/button';
import { Card } from '@/shared/ui/card';
import { Pill } from '@/shared/ui/pill';
import { Skeleton } from '@/shared/ui/skeleton';
import { useGetInvoices } from '../hooks/use-get-invoices';
import { canPayInvoice, INVOICE_STATUS } from '../model/invoice-status';
import type { InvoiceData } from '../types';

type InvoicesCardProps = {
  isPaid: boolean;
  payingInvoiceId: string | null;
  onPay: (invoice: InvoiceData) => void;
  onPdf: (invoice: InvoiceData) => void;
};

export function InvoicesCard({ isPaid, payingInvoiceId, onPay, onPdf }: InvoicesCardProps) {
  const { invoices, isLoading, error, refetch } = useGetInvoices(!isPaid);

  return (
    <Card asChild>
      <section data-enter="" aria-labelledby="inv-h" className="overflow-hidden">
        <div className="px-5 pt-4.5 pb-1.5">
          <h2 id="inv-h" className="font-semibold text-section">
            Invoices
          </h2>
        </div>
        {isPaid ? null : (
          <p className="px-5 pt-1 pb-5 text-muted">
            No invoices yet. The free hours never need a card.
          </p>
        )}
        {isPaid && isLoading ? (
          <div className="flex flex-col gap-2 px-5 pt-2 pb-5">
            <Skeleton isPulsing />
            <Skeleton isPulsing />
          </div>
        ) : null}
        {isPaid && error ? (
          <div className="px-5 pt-2 pb-5">
            <ErrorState title="Invoices did not load" onRetry={() => void refetch()} />
          </div>
        ) : null}
        {isPaid && !isLoading && !error && invoices.length === 0 ? (
          <p className="px-5 pt-1 pb-5 text-muted">
            No invoices yet. The first one comes with the first charge.
          </p>
        ) : null}
        {isPaid && invoices.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full min-w-150 border-collapse">
              <thead>
                <tr className="text-left font-medium text-meta text-muted">
                  <th className="whitespace-nowrap px-5 py-3 font-medium">Date</th>
                  <th className="whitespace-nowrap px-5 py-3 font-medium">Description</th>
                  <th className="whitespace-nowrap px-5 py-3 text-right font-medium">Amount</th>
                  <th className="whitespace-nowrap px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3">
                    <span className="sr-only">Actions</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {invoices.map((invoice) => {
                  const status = INVOICE_STATUS[invoice.status];
                  const isPayable = canPayInvoice(invoice);
                  return (
                    <tr key={invoice.id}>
                      <td className="whitespace-nowrap border-line border-t px-5 py-3 tabular-nums">
                        {formatShortDate(invoice.billedAt)}
                      </td>
                      <td className="whitespace-nowrap border-line border-t px-5 py-3">
                        {invoice.description}
                      </td>
                      <td className="whitespace-nowrap border-line border-t px-5 py-3 text-right tabular-nums">
                        {formatMoney(invoice.amountCents, invoice.currency)}
                      </td>
                      <td className="whitespace-nowrap border-line border-t px-5 py-3">
                        <Pill tone={status.tone}>{status.label}</Pill>
                      </td>
                      <td className="whitespace-nowrap border-line border-t px-5 py-3 text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          isLoading={payingInvoiceId === invoice.id}
                          onClick={() => (isPayable ? onPay(invoice) : onPdf(invoice))}
                        >
                          {isPayable ? 'Pay now' : 'PDF'}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : null}
      </section>
    </Card>
  );
}
